import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const { refreshBilling, refreshUser } = useAuth();
  const polled = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || polled.current >= 8) {
        if (!cancelled && polled.current >= 8) setStatus("failed");
        return;
      }
      polled.current += 1;
      try {
        const { data } = await axios.get(`${API}/subscription/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser(); await refreshBilling();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshBilling, refreshUser]);

  return (
    <main className="max-w-md mx-auto px-4 pt-20 pb-16 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={32} className="animate-spin text-[#2C4C3B] mx-auto" />
          <h1 className="font-serif-display text-3xl text-[#2C4C3B] mt-6">Confirming your payment…</h1>
          <p className="text-[#666] mt-2 text-sm">This usually takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="h-16 w-16 rounded-full bg-[#2C4C3B] mx-auto flex items-center justify-center">
            <Check size={28} className="text-white" />
          </div>
          <h1 className="font-serif-display text-3xl text-[#2C4C3B] mt-6">Welcome to Notch Pro.</h1>
          <p className="text-[#666] mt-2 text-sm">Unlimited plans, sharing, and photo log are unlocked.</p>
          <Link to="/" className="inline-block mt-7 px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm font-medium hover:bg-[#1E3629] transition-colors">
            Start planning →
          </Link>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <div className="h-16 w-16 rounded-full bg-[#FBEDE7] mx-auto flex items-center justify-center">
            <X size={28} className="text-[#C06F52]" />
          </div>
          <h1 className="font-serif-display text-3xl text-[#2C4C3B] mt-6">Payment not confirmed</h1>
          <p className="text-[#666] mt-2 text-sm">Don't worry — no charge if it didn't go through. Try again whenever you're ready.</p>
          <Link to="/pricing" className="inline-block mt-7 px-6 py-3 rounded-full border border-[#E5E2DC] text-[#2C4C3B] text-sm font-medium hover:bg-[#F2EFE9] transition-colors">
            Back to pricing
          </Link>
        </>
      )}
    </main>
  );
}
